export type TextStatistics = {
  characters: number;
  charactersNoSpaces: number;
  words: number;
  uniqueWords: number;
  lines: number;
  nonEmptyLines: number;
  paragraphs: number;
  sentences: number;
  bytes: number;
  readingTimeMinutes: number;
  averageWordLength: number;
  longestLineLength: number;
};

const wordPattern = /[\p{L}\p{N}]+(?:['-][\p{L}\p{N}]+)*/gu;

function countCharacters(value: string) {
  return Array.from(value).length;
}

function getWords(value: string) {
  return value.match(wordPattern) ?? [];
}

function countParagraphs(value: string) {
  const trimmed = value.trim();

  if (!trimmed) {
    return 0;
  }

  return trimmed.split(/\r?\n\s*\r?\n/).filter((paragraph) => paragraph.trim()).length;
}

function countSentences(value: string) {
  const trimmed = value.trim();

  if (!trimmed) {
    return 0;
  }

  return trimmed.match(/[^.!?\s][^.!?]*(?:[.!?]+|$)/g)?.length ?? 0;
}

export function calculateTextStatistics(value: string): TextStatistics {
  const words = getWords(value);
  const wordCharacterCount = words.reduce(
    (total, word) => total + countCharacters(word.replace(/[^\p{L}\p{N}]/gu, "")),
    0,
  );
  const lines = value.length ? value.split(/\r\n|\r|\n/) : [];
  const averageWordLength = words.length
    ? Number((wordCharacterCount / words.length).toFixed(1))
    : 0;

  return {
    characters: countCharacters(value),
    charactersNoSpaces: countCharacters(value.replace(/\s/g, "")),
    words: words.length,
    uniqueWords: new Set(words.map((word) => word.toLowerCase())).size,
    lines: lines.length,
    nonEmptyLines: lines.filter((line) => line.trim()).length,
    paragraphs: countParagraphs(value),
    sentences: countSentences(value),
    bytes: new TextEncoder().encode(value).length,
    readingTimeMinutes: words.length ? Math.max(1, Math.ceil(words.length / 200)) : 0,
    averageWordLength,
    longestLineLength: lines.reduce(
      (longest, line) => Math.max(longest, countCharacters(line)),
      0,
    ),
  };
}
